import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  Reducer,
} from "@reduxjs/toolkit";
import { Menu } from "client/jspPlatformExperiment";
import jspPlatformApi from "client/portals";
import {
  isApiError,
  isErrorResponseBodyType,
  toJspApiError,
} from "models/utils/apiUtils";
import { Dayjs } from "dayjs";

interface MenusOnDateStateProps {
  menus: Menu[];
  queryDate: Dayjs | null | undefined;
  isLoading: boolean;
  error: string | null | undefined;
}

const initialState: MenusOnDateStateProps = {
  menus: [],
  queryDate: null,
  isLoading: false,
  error: null,
};

/** Return value of menus data from the Jasper Platform API call */
const fetchMenusOnDate = createAsyncThunk(
  "menusOnDate/fetchMenus",
  async (queryDate: Dayjs, { signal, rejectWithValue }) => {
    return jspPlatformApi()
      .menu.getMenuDailyByQueryDate({
        path: { query_date: queryDate.format("YYYY-MM-DD") },
        // View-only page: skip the expensive server-side tag aggregation.
        query: { aggregate_food_tags: false },
        signal, // cancel the HTTP request when the thunk is aborted (e.g. date changed)
      })
      .then((res) => res.data ?? [])
      .catch((error) => {
        let errMsg: string;
        const err = isApiError(error) ? toJspApiError(error) : null;
        if (err && isErrorResponseBodyType(err.body)) {
          errMsg = err.body.detail;
        } else {
          errMsg = "Unknown error!";
        }
        return rejectWithValue(errMsg);
      });
  },
);

const setMenus = (
  state: MenusOnDateStateProps,
  action: PayloadAction<Menu[] | null>,
): MenusOnDateStateProps => {
  if (action.payload) {
    return { ...state, menus: action.payload };
  }
  return initialState;
};

export const menusOnDateSlice = createSlice({
  name: "menuDetailSlice",
  initialState,
  reducers: {
    setMenus,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMenusOnDate.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(fetchMenusOnDate.fulfilled, (state, action) => {
      return {
        ...state,
        ...setMenus(state, action),
        isLoading: false,
      };
    });
    builder.addCase(fetchMenusOnDate.rejected, (state, action) => {
      if (!action.payload) {
        return {
          ...state,
          isLoading: true,
          error: null,
        };
      }
      return {
        ...state,
        isLoading: false,
        error: action.payload as string,
      };
    });
  },
});

export const MenusOnDateActions = {
  ...menusOnDateSlice.actions,
  fetchMenusOnDate,
};

export const MenuOnDateReducer: Reducer<MenusOnDateStateProps> =
  menusOnDateSlice.reducer;

export default MenuOnDateReducer;
